import { useState, useEffect, useMemo } from "react";
import { Wand2, Loader2, Plus, X, Check } from "lucide-react";
import { toast } from "sonner";
import { HEADLINE_FONTS, SCRIPT_FONTS, BODY_FONTS, TITLE_SIZES, HOSTS_SIZES, SUBTITLE_SIZES, BODY_SIZES } from "@/lib/fonts";
import { buildThemeFromImage, buildBackgroundOptions } from "@/lib/palette";
import { resolveImageUrl } from "@/components/ImageUpload";
import { loadGoogleFont, mergeCustomFonts, resolveGoogleFont, suggestFamily } from "@/lib/googleFonts";

export default function StyleCustomizer({ style, onChange, heroImageUrl }) {
  const s = style || {};
  const patch = (p) => onChange({ ...s, ...p });
  const [suggesting, setSuggesting] = useState(false);
  const [suggestedPalette, setSuggestedPalette] = useState(null);
  const [variants, setVariants] = useState([]);
  const [swatchTarget, setSwatchTarget] = useState("text");

  const customFonts = s.custom_fonts || [];

  // Ensure every stored custom font is actually loaded via <link>
  useEffect(() => {
    customFonts.forEach((f) => f && f.family && loadGoogleFont(f.family));
  }, [customFonts]);

  // Warn about saved custom fonts that Google Fonts does not serve (typos fall back to a system font)
  const [badFonts, setBadFonts] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const bad = [];
      for (const f of customFonts) {
        if (!f?.family) continue;
        // eslint-disable-next-line no-await-in-loop
        const { available } = await resolveGoogleFont(f.family);
        if (!available) bad.push({ family: f.family, id: f.id, category: f.category });
      }
      if (!cancelled) setBadFonts(bad);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(customFonts.map((f) => f.family))]);

  // Replace a saved (broken) custom font everywhere it is referenced
  const replaceCustomFont = (bad, newFamily) => {
    const next = customFonts
      .filter((f) => !(f.id === bad.id && f.category === bad.category))
      .concat(newFamily ? [{ id: newFamily, family: newFamily, label: newFamily, category: bad.category }] : []);
    const p = { custom_fonts: next };
    ["headline_font", "script_font", "body_font", "hosts_font", "message_font"].forEach((k) => {
      if (s[k] === bad.id) p[k] = newFamily || "";
    });
    if (newFamily) loadGoogleFont(newFamily);
    patch(p);
    toast.success(newFamily ? `Tipografía corregida a "${newFamily}"` : `"${bad.family}" eliminada`);
  };

  const addCustomFont = async (category, family) => {
    const clean = (family || "").trim().replace(/\s+/g, " ");
    if (!clean) return;
    const { available, resolved } = await resolveGoogleFont(clean);
    if (!available) {
      const hint = suggestFamily(clean);
      toast.error(
        hint
          ? `No encontramos "${clean}" en Google Fonts. ¿Quisiste decir "${hint}"?`
          : `No encontramos "${clean}" en Google Fonts. Revisa el nombre exacto en fonts.google.com`
      );
      return;
    }
    const id = resolved || clean;
    if (customFonts.some((f) => f.id?.toLowerCase() === id.toLowerCase() && f.category === category)) {
      toast.info("Esa tipografía ya está agregada");
      return;
    }
    loadGoogleFont(id);
    const next = [...customFonts, { id, family: id, label: id, category }];
    patch({ custom_fonts: next });
    toast.success(`Tipografía "${id}" añadida`);
  };

  const removeCustomFont = (category, id) => {
    const next = customFonts.filter((f) => !(f.id === id && f.category === category));
    patch({ custom_fonts: next });
  };

  const handleSuggest = async () => {
    if (!heroImageUrl) { toast.error("Primero elige o genera una imagen de portada"); return; }
    try {
      setSuggesting(true);
      const url = resolveImageUrl(heroImageUrl);
      const { palette, variants: vs, ...theme } = await buildThemeFromImage(url);
      setSuggestedPalette(palette);
      setVariants(vs);
      patch(theme);
      toast.success("Paleta completa aplicada desde tu imagen");
    } catch {
      toast.error("No se pudo leer la imagen. Prueba con otra portada.");
    } finally {
      setSuggesting(false);
    }
  };

  // Precompute the 3 variants so the user can switch with a single tap
  useEffect(() => {
    let cancelled = false;
    if (!heroImageUrl) { setVariants([]); return; }
    (async () => {
      try {
        const { palette, variants: vs } = await buildThemeFromImage(resolveImageUrl(heroImageUrl));
        if (cancelled) return;
        setVariants(vs);
        setSuggestedPalette(palette);
      } catch { /* image not readable (CORS) — variants stay hidden */ }
    })();
    return () => { cancelled = true; };
  }, [heroImageUrl]);

  const applyVariant = (v) => {
    const { id, label, description, ...colors } = v;
    patch(colors);
    toast.success(`Paleta ${label.toLowerCase()} aplicada`);
  };

  const activeVariantId = variants.find(
    (v) => v.accent_color === s.accent_color && v.bg_color === s.bg_color && v.text_color === s.text_color
  )?.id;

  // Background suggestions — each one already paired with a legible text color
  const bgOptions = useMemo(
    () => (suggestedPalette ? buildBackgroundOptions(suggestedPalette, s.text_color, s.accent_color) : []),
    [suggestedPalette, s.text_color, s.accent_color]
  );

  const applyBackground = (opt) => {
    patch({
      bg_color: opt.bg_color,
      surface_color: opt.surface_color,
      muted_color: opt.muted_color,
      text_color: opt.text_color,
      accent_color: opt.accent_color,
    });
    toast.success(
      opt.adjustedText
        ? `Fondo ${opt.label.toLowerCase()} aplicado — ajustamos el texto para que se lea bien`
        : `Fondo ${opt.label.toLowerCase()} aplicado`
    );
  };

  const resetColors = () => {
    patch({ text_color: "", accent_color: "", bg_color: "", surface_color: "", muted_color: "" });
    toast.success("Colores restablecidos");
  };

  const applySwatch = (hex) => {
    if (swatchTarget === "text") patch({ text_color: hex });
    else if (swatchTarget === "accent") patch({ accent_color: hex });
    else patch({ bg_color: hex });
  };

  const headlineOpts = mergeCustomFonts(HEADLINE_FONTS, customFonts, "headline", "serif");
  const scriptOpts = mergeCustomFonts(SCRIPT_FONTS, customFonts, "script", "cursive");
  const bodyOpts = mergeCustomFonts(BODY_FONTS, customFonts, "body", "sans-serif");
  // Hosts & message can be any font — merge all curated lists so the picker shows serif + script + sans
  const ALL_CURATED = [...HEADLINE_FONTS, ...SCRIPT_FONTS, ...BODY_FONTS];
  const hostsOpts = mergeCustomFonts(ALL_CURATED, customFonts, "hosts", "sans-serif");
  const messageOpts = mergeCustomFonts(ALL_CURATED, customFonts, "message", "serif");
  const themeLabelOpts = mergeCustomFonts(ALL_CURATED, customFonts, "theme_label", "sans-serif");

  return (
    <div className="space-y-8">
      {badFonts.length > 0 && (
        <div className="rounded-2xl border border-red-300/60 bg-red-50/70 px-4 py-3" data-testid="bad-fonts-warning">
          <p className="text-xs font-semibold uppercase tracking-widest text-red-700">Tipografía no encontrada</p>
          <p className="text-xs text-red-700/90 mt-1 leading-relaxed">
            Google Fonts no tiene {badFonts.map((f) => `"${f.family}"`).join(", ")}, así que esos textos se ven con la letra por defecto del teléfono.
          </p>
          <div className="mt-3 space-y-2">
            {badFonts.map((bad) => {
              const hint = suggestFamily(bad.family);
              return (
                <div key={`${bad.category}-${bad.id}`} className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-red-800">{bad.family}</span>
                  {hint && (
                    <button
                      type="button"
                      onClick={() => replaceCustomFont(bad, hint)}
                      data-testid={`fix-bad-font-${bad.id}`}
                      className="text-xs rounded-full px-3 py-1 bg-red-600 text-white hover:bg-red-700 transition-colors"
                    >
                      Corregir a “{hint}”
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => replaceCustomFont(bad, null)}
                    data-testid={`remove-bad-font-${bad.id}`}
                    className="text-xs rounded-full px-3 py-1 bg-white text-red-700 border border-red-300 hover:bg-red-100 transition-colors"
                  >
                    Eliminar
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <FontSizeGroup
        label="Título"
        fontValue={s.headline_font} onFontChange={(v) => patch({ headline_font: v })} fontOptions={headlineOpts}
        sizeValue={s.title_size} onSizeChange={(v) => patch({ title_size: v })} sizeOptions={TITLE_SIZES}
        scaleValue={s.title_scale} onScaleChange={(v) => patch({ title_scale: v })}
        testid="title" preview="Ana & Luis"
        onAdd={(fam) => addCustomFont("headline", fam)} onRemove={(id) => removeCustomFont("headline", id)}
        customFonts={customFonts.filter((f) => f.category === "headline")}
        helperText="El texto grande de la portada."
        extra={
          <HeroCoverControls
            s={s} patch={patch} prefix="title" testidBase="title"
            strokeSteps={[
              { v: 0, label: "Sin borde", key: "none" },
              { v: 2, label: "Fino", key: "thin" },
              { v: 3.5, label: "Medio", key: "medium" },
              { v: 5, label: "Grueso", key: "thick" },
            ]}
            strokeDefault={3.5} glowDefault="medium"
          />
        }
      />

      <FontSizeGroup
        label="Subtítulo (cursiva)"
        fontValue={s.script_font} onFontChange={(v) => patch({ script_font: v })} fontOptions={scriptOpts}
        sizeValue={s.subtitle_size} onSizeChange={(v) => patch({ subtitle_size: v })} sizeOptions={SUBTITLE_SIZES}
        scaleValue={s.subtitle_scale} onScaleChange={(v) => patch({ subtitle_scale: v })}
        colorValue={s.subtitle_color} onColorChange={(v) => patch({ subtitle_color: v })}
        colorLabel="Color del subtítulo"
        colorPlaceholder={s.accent_color || "#C5A880"}
        colorTestid="subtitle-color"
        testid="subtitle" preview="para siempre"
        onAdd={(fam) => addCustomFont("script", fam)} onRemove={(id) => removeCustomFont("script", id)}
        customFonts={customFonts.filter((f) => f.category === "script")}
        extra={
          <div className="space-y-4">
            <div>
              <span className="text-xs tracking-widest uppercase text-inksoft">Borde de las letras</span>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="subtitle-stroke-group">
                {[
                  { v: 0, label: "Sin borde", key: "none" },
                  { v: 1, label: "Fino", key: "thin" },
                  { v: 2, label: "Medio", key: "medium" },
                  { v: 3.5, label: "Grueso", key: "thick" },
                ].map((o) => {
                  const active = Number(s.subtitle_stroke_width || 0) === o.v;
                  return (
                    <button
                      key={o.label}
                      type="button"
                      onClick={() => patch({ subtitle_stroke_width: o.v })}
                      data-testid={`subtitle-stroke-${o.key}`}
                      className={`text-xs rounded-full px-4 py-2 border transition-colors ${
                        active ? "bg-warmgold text-cream border-warmgold" : "bg-white/70 text-ink border-warmgold/40 hover:bg-white"
                      }`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {Number(s.subtitle_stroke_width || 0) > 0 && (
              <ColorRow
                label="Color del borde"
                value={s.subtitle_stroke_color}
                onChange={(v) => patch({ subtitle_stroke_color: v })}
                placeholder="#1E1814"
                testid="subtitle-stroke-color"
              />
            )}
          </div>
        }
      />

      <FontSizeGroup
        label="Etiqueta del hero"
        helperText="El texto pequeño hasta arriba de la portada (por ejemplo “Una celebración especial”)."
        fontValue={s.theme_label_font} onFontChange={(v) => patch({ theme_label_font: v })} fontOptions={themeLabelOpts}
        sizeValue={s.theme_label_size} onSizeChange={(v) => patch({ theme_label_size: v })} sizeOptions={SUBTITLE_SIZES}
        scaleValue={s.theme_label_scale} onScaleChange={(v) => patch({ theme_label_scale: v })}
        colorValue={s.theme_label_color} onColorChange={(v) => patch({ theme_label_color: v })}
        colorLabel="Color del texto"
        colorPlaceholder="#3E3A35"
        colorTestid="theme-label-color"
        colorResetLabel="Usar el color por defecto"
        testid="theme-label" preview="Una celebración"
        onAdd={(fam) => addCustomFont("theme_label", fam)} onRemove={(id) => removeCustomFont("theme_label", id)}
        customFonts={customFonts.filter((f) => f.category === "theme_label")}
        extra={
          <div className="space-y-4">
            <div>
              <span className="text-xs tracking-widest uppercase text-inksoft">Borde de las letras</span>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="theme-label-stroke-group">
                {[
                  { v: 0, label: "Sin borde", key: "none" },
                  { v: 0.6, label: "Fino", key: "thin" },
                  { v: 1.2, label: "Medio", key: "medium" },
                  { v: 2, label: "Grueso", key: "thick" },
                ].map((o) => {
                  const active = Number(s.theme_label_stroke_width || 0) === o.v;
                  return (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => patch({ theme_label_stroke_width: o.v })}
                      data-testid={`theme-label-stroke-${o.key}`}
                      className={`text-xs rounded-full px-4 py-2 border transition-colors ${
                        active ? "bg-warmgold text-cream border-warmgold" : "bg-white/70 text-ink border-warmgold/40 hover:bg-white"
                      }`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {Number(s.theme_label_stroke_width || 0) > 0 && (
              <ColorRow
                label="Color del borde"
                value={s.theme_label_stroke_color}
                onChange={(v) => patch({ theme_label_stroke_color: v })}
                placeholder="#1E1814"
                testid="theme-label-stroke-color"
              />
            )}
          </div>
        }
      />


      <FontSizeGroup
        label="Anfitriones"
        fontValue={s.hosts_font} onFontChange={(v) => patch({ hosts_font: v })} fontOptions={hostsOpts}
        sizeValue={s.hosts_size} onSizeChange={(v) => patch({ hosts_size: v })} sizeOptions={HOSTS_SIZES}
        scaleValue={s.hosts_scale} onScaleChange={(v) => patch({ hosts_scale: v })}
        testid="hosts" preview="Ana & Luis"
        onAdd={(fam) => addCustomFont("hosts", fam)} onRemove={(id) => removeCustomFont("hosts", id)}
        customFonts={customFonts.filter((f) => f.category === "hosts")}
        helperText="El nombre de los anfitriones en la portada."
        extra={
          <HeroCoverControls
            s={s} patch={patch} prefix="hosts" testidBase="hosts"
            strokeSteps={[
              { v: 0, label: "Sin borde", key: "none" },
              { v: 1.5, label: "Fino", key: "thin" },
              { v: 3, label: "Medio", key: "medium" },
              { v: 4.5, label: "Grueso", key: "thick" },
            ]}
            strokeDefault={3} glowDefault="medium"
          />
        }
      />

      <FontSizeGroup
        label="Mensaje personal y títulos de sección"
        fontValue={s.message_font} onFontChange={(v) => patch({ message_font: v })} fontOptions={messageOpts}
        sizeValue={s.message_size} onSizeChange={(v) => patch({ message_size: v })} sizeOptions={TITLE_SIZES}
        scaleValue={s.message_scale} onScaleChange={(v) => patch({ message_scale: v })}
        testid="message" preview="Nos casamos, ven a celebrar."
        onAdd={(fam) => addCustomFont("message", fam)} onRemove={(id) => removeCustomFont("message", id)}
        customFonts={customFonts.filter((f) => f.category === "message")}
        helperText="Se aplica al mensaje personal, títulos de secciones (RESERVA LA FECHA, NOS VEMOS AQUÍ...) y textos del countdown."
      />

      <FontSizeGroup
        label="Cuerpo (textos generales)"
        fontValue={s.body_font} onFontChange={(v) => patch({ body_font: v })} fontOptions={bodyOpts}
        sizeValue={s.body_size} onSizeChange={(v) => patch({ body_size: v })} sizeOptions={BODY_SIZES}
        scaleValue={s.body_scale} onScaleChange={(v) => patch({ body_scale: v })}
        testid="body" preview="Sábado 14 de mayo · 5:00 PM"
        onAdd={(fam) => addCustomFont("body", fam)} onRemove={(id) => removeCustomFont("body", id)}
        customFonts={customFonts.filter((f) => f.category === "body")}
      />

      <div>
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <span className="text-xs tracking-widest uppercase text-inksoft">Colores</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={resetColors}
              data-testid="reset-palette-btn"
              className="rounded-full px-3 py-1.5 text-[11px] uppercase tracking-widest font-medium text-inksoft border border-warmgold/30 hover:text-ink hover:border-warmgold transition-colors"
            >
              Restablecer
            </button>
            <button
              type="button"
              onClick={handleSuggest}
              disabled={suggesting || !heroImageUrl}
              data-testid="suggest-palette-btn"
              className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-widest font-medium bg-warmgold/10 text-warmgold border border-warmgold/40 hover:bg-warmgold/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Extrae los colores dominantes de la portada"
            >
              {suggesting ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
              {suggesting ? "Analizando..." : "Sugerir desde portada"}
            </button>
          </div>
        </div>
        {variants.length > 0 && (
          <div className="mb-5" data-testid="palette-variants">
            <p className="text-[11px] text-inksoft mb-3">
              Paletas tomadas de tu portada — toca una para aplicarla y luego ajusta cualquier color abajo.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {variants.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => applyVariant(v)}
                  data-testid={`palette-variant-${v.id}`}
                  className={`rounded-2xl border p-3 text-left transition-all hover:-translate-y-0.5 ${
                    activeVariantId === v.id ? "border-warmgold ring-2 ring-warmgold/30" : "border-warmgold/25 hover:border-warmgold"
                  }`}
                  style={{ background: v.bg_color }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: v.text_color }}>{v.label}</span>
                    {activeVariantId === v.id && <Check size={14} style={{ color: v.accent_color }} />}
                  </div>
                  <span className="block text-[10px] mt-1 leading-snug" style={{ color: v.muted_color }}>{v.description}</span>
                  <span className="mt-3 flex gap-1.5">
                    {[v.bg_color, v.surface_color, v.accent_color, v.text_color, v.muted_color].map((hex, i) => (
                      <span key={i} className="w-5 h-5 rounded-full border border-black/10" style={{ background: hex }} />
                    ))}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
        {bgOptions.length > 0 && (
          <div className="mb-5" data-testid="bg-options">
            <p className="text-[11px] text-inksoft mb-3">
              Fondos sugeridos para tu invitación — el texto se ajusta solo para que nunca choque con el fondo.
            </p>
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
              {bgOptions.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => applyBackground(opt)}
                  data-testid={`bg-option-${opt.id}`}
                  className={`shrink-0 w-[112px] rounded-2xl border p-3 text-center transition-all hover:-translate-y-0.5 ${
                    s.bg_color === opt.bg_color ? "border-warmgold ring-2 ring-warmgold/30" : "border-warmgold/25 hover:border-warmgold"
                  }`}
                  style={{ background: opt.bg_color }}
                >
                  <span className="block text-2xl leading-none" style={{ color: opt.text_color, fontFamily: "var(--fn-headline, 'Playfair Display', serif)" }}>Aa</span>
                  <span className="block text-[10px] mt-2 uppercase tracking-widest" style={{ color: opt.text_color }}>{opt.label}</span>
                  <span className="block text-[9px] mt-0.5" style={{ color: opt.muted_color }}>contraste {opt.contrast}:1</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <ColorRow label="Color del texto" value={s.text_color} onChange={(v) => patch({ text_color: v })} placeholder="#3E3A35" testid="color-text" />
          <ColorRow label="Color de acento" value={s.accent_color} onChange={(v) => patch({ accent_color: v })} placeholder="#C5A880" testid="color-accent" />
          <ColorRow label="Fondo de la invitación" value={s.bg_color} onChange={(v) => patch({ bg_color: v })} placeholder="#FDFBF7" testid="color-bg" />
          <ColorRow label="Tarjetas y bloques" value={s.surface_color} onChange={(v) => patch({ surface_color: v })} placeholder="#F5F2EB" testid="color-surface" />
          <ColorRow label="Texto secundario" value={s.muted_color} onChange={(v) => patch({ muted_color: v })} placeholder="#7A7369" testid="color-muted" />
        </div>
        {suggestedPalette && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] text-inksoft">Aplicar al:</p>
              <div className="inline-flex rounded-full bg-white/60 border border-warmgold/30 p-0.5" data-testid="swatch-target-toggle">
                <button type="button" onClick={() => setSwatchTarget("text")} className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-widest font-medium transition-colors ${swatchTarget === "text" ? "bg-warmgold text-white" : "text-inksoft hover:text-ink"}`} data-testid="swatch-target-text">Texto</button>
                <button type="button" onClick={() => setSwatchTarget("accent")} className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-widest font-medium transition-colors ${swatchTarget === "accent" ? "bg-warmgold text-white" : "text-inksoft hover:text-ink"}`} data-testid="swatch-target-accent">Acento</button>
                <button type="button" onClick={() => setSwatchTarget("bg")} className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-widest font-medium transition-colors ${swatchTarget === "bg" ? "bg-warmgold text-white" : "text-inksoft hover:text-ink"}`} data-testid="swatch-target-bg">Fondo</button>
              </div>
            </div>
            <p className="text-[11px] text-inksoft mb-2">Toca un color para aplicarlo al <span className="text-ink font-medium">{swatchTarget === "text" ? "color del texto" : swatchTarget === "accent" ? "color de acento" : "fondo"}</span>:</p>
            <div className="flex gap-2 flex-wrap" data-testid="suggested-palette-swatches">
              {suggestedPalette.all.slice(0, 8).map((hex) => (
                <button key={hex} onClick={() => applySwatch(hex)} className="w-9 h-9 rounded-full border-2 border-white shadow hover:scale-110 transition-transform" style={{ background: hex }} title={hex} data-testid={`palette-swatch-${hex.replace("#", "")}`} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function HeroCoverControls({ s, patch, prefix, strokeSteps, strokeDefault, glowDefault, testidBase }) {
  const strokeKey = `${prefix}_stroke_width`;
  const strokeColorKey = `${prefix}_stroke_color`;
  const glowKey = `${prefix}_glow`;
  const glowColorKey = `${prefix}_glow_color`;
  const currentStroke = s[strokeKey] != null && s[strokeKey] !== "" ? Number(s[strokeKey]) : strokeDefault;
  const currentGlow = s[glowKey] || glowDefault;
  const colorPlaceholder = s.text_color || "#3E3A35";
  const glowSteps = [
    { v: "none", label: "Sin difuminado" },
    { v: "soft", label: "Suave" },
    { v: "medium", label: "Medio" },
    { v: "strong", label: "Fuerte" },
  ];
  return (
    <div className="space-y-4">
      <div>
        <span className="text-xs tracking-widest uppercase text-inksoft">Ancho del borde de las letras</span>
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`${testidBase}-stroke-group`}>
          {strokeSteps.map((o) => {
            const active = Number(currentStroke) === o.v;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => patch({ [strokeKey]: o.v })}
                data-testid={`${testidBase}-stroke-${o.key}`}
                className={`text-xs rounded-full px-4 py-2 border transition-colors ${
                  active ? "bg-warmgold text-cream border-warmgold" : "bg-white/70 text-ink border-warmgold/40 hover:bg-white"
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
      {currentStroke > 0 && (
        <ColorRow
          label="Color del borde"
          value={s[strokeColorKey]}
          onChange={(v) => patch({ [strokeColorKey]: v })}
          placeholder={colorPlaceholder}
          testid={`${testidBase}-stroke-color`}
        />
      )}
      <div>
        <span className="text-xs tracking-widest uppercase text-inksoft">Difuminado detrás del texto</span>
        <p className="text-[10px] text-inksoft/80 mt-1 leading-snug">La nube de color que aparece detrás. Bájala o quítala si te parece muy grande.</p>
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`${testidBase}-glow-group`}>
          {glowSteps.map((o) => {
            const active = currentGlow === o.v;
            return (
              <button
                key={o.v}
                type="button"
                onClick={() => patch({ [glowKey]: o.v })}
                data-testid={`${testidBase}-glow-${o.v}`}
                className={`text-xs rounded-full px-4 py-2 border transition-colors ${
                  active ? "bg-warmgold text-cream border-warmgold" : "bg-white/70 text-ink border-warmgold/40 hover:bg-white"
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
      {currentGlow !== "none" && (
        <ColorRow
          label="Color del difuminado"
          value={s[glowColorKey]}
          onChange={(v) => patch({ [glowColorKey]: v })}
          placeholder={colorPlaceholder}
          testid={`${testidBase}-glow-color`}
        />
      )}
    </div>
  );
}

function FontSizeGroup({ label, fontValue, onFontChange, fontOptions, sizeValue, onSizeChange, sizeOptions, scaleValue, onScaleChange, testid, preview, onAdd, onRemove, customFonts, helperText, colorValue, onColorChange, colorLabel, colorPlaceholder, colorTestid, colorResetLabel, extra }) {
  return (
    <div className="rounded-2xl border border-warmgold/25 bg-white/30 p-4 space-y-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h4 className="text-sm font-semibold uppercase tracking-widest text-ink">{label}</h4>
        {helperText && <p className="text-[10px] text-inksoft max-w-xs text-right leading-snug">{helperText}</p>}
      </div>
      <FontRow
        label="Tipografía"
        value={fontValue}
        onChange={onFontChange}
        options={fontOptions}
        testid={`font-${testid}`}
        preview={preview}
        onAdd={onAdd}
        onRemove={onRemove}
        customFonts={customFonts}
      />
      {sizeOptions && (
        <SizeRow
          label="Tamaño"
          value={sizeValue}
          onChange={onSizeChange}
          options={sizeOptions}
          scaleValue={scaleValue}
          onScaleChange={onScaleChange}
          testid={`${testid}-size`}
        />
      )}
      {onColorChange && (
        <div>
          <ColorRow
            label={colorLabel || "Color"}
            value={colorValue}
            onChange={onColorChange}
            placeholder={colorPlaceholder || "#C5A880"}
            testid={colorTestid || `${testid}-color`}
          />
          {colorValue && (
            <button
              type="button"
              onClick={() => onColorChange("")}
              className="mt-2 text-[11px] text-inksoft hover:text-ink"
              data-testid={`${colorTestid || `${testid}-color`}-reset`}
            >
              {colorResetLabel || "Usar el color de acento"}
            </button>
          )}
        </div>
      )}
      {extra}
    </div>
  );
}

function FontRow({ label, value, onChange, options, testid, preview, onAdd, onRemove, customFonts }) {
  const active = options.find((o) => o.id === value) || null;
  const [adding, setAdding] = useState(false);
  const [family, setFamily] = useState("");
  const submit = () => {
    const clean = family.trim();
    if (!clean) return;
    onAdd?.(clean);
    setFamily("");
    setAdding(false);
  };
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-xs tracking-widest uppercase text-inksoft">{label}</span>
        <div className="flex items-center gap-2">
          {value && (
            <button onClick={() => onChange("")} className="text-[11px] text-inksoft hover:text-ink" data-testid={`${testid}-reset`}>Reset</button>
          )}
          <button
            type="button"
            onClick={() => setAdding((a) => !a)}
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-widest font-medium bg-warmgold/10 text-warmgold border border-warmgold/40 hover:bg-warmgold/20 transition-colors"
            data-testid={`${testid}-add-btn`}
          >
            <Plus size={11} /> Agregar
          </button>
        </div>
      </div>
      {adding && (
        <div className="mt-3 flex items-center gap-2 rounded-2xl border border-warmgold/40 bg-white/70 p-2">
          <input
            type="text"
            value={family}
            onChange={(e) => setFamily(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            placeholder="Nombre de Google Font (ej: Merriweather)"
            className="flex-1 bg-transparent focus:outline-none px-2 py-1 text-sm text-ink placeholder:text-inksoft/60"
            data-testid={`${testid}-add-input`}
            autoFocus
          />
          <button onClick={submit} className="px-3 py-1.5 rounded-full bg-warmgold text-white text-xs uppercase tracking-widest font-medium hover:opacity-90" data-testid={`${testid}-add-confirm`}>Añadir</button>
          <button onClick={() => { setAdding(false); setFamily(""); }} className="p-1 text-inksoft hover:text-ink" aria-label="Cancelar"><X size={14} /></button>
        </div>
      )}
      <div className="mt-3 flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
        {options.map((f) => {
          const isCustom = customFonts?.some((c) => c.id === f.id);
          return (
            <div key={f.id} className="relative shrink-0">
              <button
                onClick={() => onChange(f.id)}
                data-testid={`${testid}-${f.id.replace(/\s+/g, "-")}`}
                className={`rounded-2xl border px-4 py-3 text-left transition-all min-w-[150px] ${value === f.id ? "border-warmgold bg-warmgold/10" : "border-warmgold/25 bg-white/40 hover:border-warmgold"}`}
              >
                <span className="block text-2xl text-ink leading-none" style={{ fontFamily: f.css }}>{preview}</span>
                <span className="block text-[10px] text-inksoft mt-2 uppercase tracking-widest">{f.label}</span>
              </button>
              {isCustom && onRemove && (
                <button
                  onClick={(e) => { e.stopPropagation(); onRemove(f.id); }}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-warmgold/40 grid place-items-center text-inksoft hover:text-red-500 hover:border-red-300 shadow-sm"
                  aria-label={`Quitar ${f.label}`}
                  data-testid={`${testid}-remove-${f.id.replace(/\s+/g, "-")}`}
                >
                  <X size={10} />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {active && <p className="text-[11px] text-inksoft mt-1">Activo: <span className="text-ink">{active.label}</span></p>}
    </div>
  );
}

function SizeRow({ label, value, onChange, options, scaleValue, onScaleChange, testid }) {
  const presetOf = (id) => options.find((t) => t.id === id)?.scale;
  const effScale = (scaleValue != null && scaleValue !== "" && Number(scaleValue) > 0)
    ? Number(scaleValue)
    : (presetOf(value) ?? 1);
  const scales = options.map((o) => o.scale);
  const sliderMin = Math.round(Math.min(...scales) * 0.7 * 100) / 100;
  const sliderMax = Math.round(Math.max(...scales) * 1.4 * 100) / 100;
  const pickPreset = (id) => {
    onChange(id);
    onScaleChange?.(presetOf(id) ?? 0);
  };
  return (
    <div>
      <span className="text-xs tracking-widest uppercase text-inksoft">{label}</span>
      <div className="mt-3 flex gap-2 flex-wrap">
        {options.map((t) => {
          const active = Math.abs(effScale - t.scale) < 0.001;
          return (
            <button key={t.id} onClick={() => pickPreset(t.id)} data-testid={`${testid}-${t.id}`} className={`px-4 py-2 rounded-full border text-sm transition-all ${active ? "bg-warmgold text-white border-warmgold" : "bg-white/50 border-warmgold/30 text-ink hover:border-warmgold"}`}>{t.label}</button>
          );
        })}
        <button onClick={() => { onChange(""); onScaleChange?.(0); }} className="px-3 py-2 rounded-full text-xs text-inksoft hover:text-ink" data-testid={`${testid}-reset`}>Reset</button>
      </div>
      {onScaleChange && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-[11px] text-inksoft">
            <span>Ajuste fino</span>
            <span data-testid={`${testid}-scale-value`}>{Math.round(effScale * 100)}%</span>
          </div>
          <input
            type="range"
            min={sliderMin}
            max={sliderMax}
            step={0.01}
            value={effScale}
            onChange={(e) => onScaleChange(Number(e.target.value))}
            className="mt-1 w-full accent-warmgold cursor-pointer"
            data-testid={`${testid}-scale-slider`}
          />
        </div>
      )}
    </div>
  );
}

function ColorRow({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-xs tracking-widest uppercase text-inksoft">{label}</span>
      <div className="mt-2 flex items-center gap-3">
        <input type="color" value={value || placeholder} onChange={(e) => onChange(e.target.value)} className="w-12 h-12 rounded-full border-2 border-white shadow cursor-pointer" data-testid={`${testid}-picker`} />
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="flex-1 bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink text-sm" data-testid={`${testid}-hex`} />
      </div>
    </label>
  );
}
